import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Handshake,
  FileSignature,
  Sparkles,
  ArrowRightLeft,
  Megaphone,
  Compass,
  Trophy,
} from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  handshake: Handshake,
  "file-signature": FileSignature,
  sparkles: Sparkles,
  "arrow-right-left": ArrowRightLeft,
  megaphone: Megaphone,
  compass: Compass,
  trophy: Trophy,
};

export default function DeptServices() {
  const { dept } = useOutletContext();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/services", { params: { department: dept.slug } }).then((r) => setItems(r.data));
  }, [dept.slug]);

  // Split into "on the pitch" (first half) and "off the pitch" (second half)
  const onPitch = items.slice(0, Math.ceil(items.length / 2));
  const offPitch = items.slice(Math.ceil(items.length / 2));

  return (
    <div className="pt-20 md:pt-28 pb-24">
      <header className="text-center max-w-4xl mx-auto px-6 mb-16 md:mb-24">
        <div className="csg-label" style={{ color: dept.accent }}>
          Services
        </div>
        <h1 className="font-display uppercase leading-[0.9] mt-5 text-5xl md:text-[7vw]">
          <span className="block text-white">What We</span>
          <span className="block" style={{ color: dept.accent }}>
            Actually Do.
          </span>
        </h1>
        <p className="font-body text-base md:text-lg text-white/65 mt-7">
          Holistic representation across the full lifecycle of an athletic career — on the pitch
          and off it.
        </p>
      </header>

      <ServiceBlock
        label="On The Pitch"
        title={["Delivering For", "You On The Pitch"]}
        copy="Industry leaders across the UK, Europe and beyond delivering some of the world's biggest transfers for over 15 years."
        items={onPitch}
        dept={dept}
      />

      <ServiceBlock
        label="Off The Pitch"
        title={["Delivering For", "You Off The Pitch"]}
        copy="Working with the biggest brands, platforms and broadcasters to deliver off the pitch as well as on it."
        items={offPitch}
        dept={dept}
        reverse
      />
    </div>
  );
}

function ServiceBlock({ label, title, copy, items, dept, reverse }) {
  return (
    <section className="relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${dept.hero_image})` }}
      />
      <div className="absolute inset-0 bg-black/85" />

      <div
        className={`relative max-w-[1500px] mx-auto px-6 md:px-14 py-20 md:py-28 grid md:grid-cols-12 gap-10 ${
          reverse ? "md:[direction:rtl]" : ""
        }`}
      >
        <div className="md:col-span-4 [direction:ltr]">
          <div className="csg-label" style={{ color: dept.accent }}>
            {label}
          </div>
          <h2 className="font-display uppercase leading-[0.9] mt-5 text-4xl md:text-6xl">
            <span className="block text-white">{title[0]}</span>
            <span className="block" style={{ color: dept.accent }}>
              {title[1]}
            </span>
          </h2>
          <p className="font-body text-white/65 mt-6 max-w-md">{copy}</p>
        </div>

        <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-4 [direction:ltr]">
          {items.map((s) => {
            const Icon = ICONS[s.icon] || Trophy;
            const [first, ...rest] = s.title.split(" ");
            return (
              <article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="csg-card group p-6 aspect-square flex flex-col justify-between"
                style={{ "--csg-accent": dept.accent }}
              >
                <Icon size={28} strokeWidth={1.4} style={{ color: dept.accent }} />
                <div>
                  <div className="csg-label" style={{ color: dept.accent }}>
                    {first}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl uppercase mt-1 leading-[0.95]">
                    {rest.join(" ") || first}
                  </h3>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
